<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue';
import type { Video } from '@/data/help';

const props = defineProps<{
  video: Video;
}>();

const videoRef = ref<HTMLVideoElement | null>(null);
const isPlaying = ref(false);
const currentTime = ref(0);
const duration = ref(0);
const isLoaded = ref(false);
const isError = ref(false);
const isFullscreen = ref(false);

// Build full path - videos are in public/help/videos/
const videoPath = computed(() => {
  return `/help/videos/${props.video.src}`;
});

const posterPath = computed(() => {
  if (props.video.poster) {
    return `/help/videos/${props.video.poster}`;
  }
  return undefined;
});

const progress = computed(() => {
  if (duration.value === 0) return 0;
  return (currentTime.value / duration.value) * 100;
});

const formattedTime = computed(() => {
  const format = (time: number) => {
    const mins = Math.floor(time / 60);
    const secs = Math.floor(time % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };
  return `${format(currentTime.value)} / ${format(duration.value)}`;
});

const isGif = computed(() => {
  return props.video.src.toLowerCase().endsWith('.gif');
});

function togglePlay() {
  if (!videoRef.value) return;

  if (isPlaying.value) {
    videoRef.value.pause();
  } else {
    videoRef.value.play();
  }
}

function handlePlay() {
  isPlaying.value = true;
}

function handlePause() {
  isPlaying.value = false;
}

function handleTimeUpdate() {
  if (videoRef.value) {
    currentTime.value = videoRef.value.currentTime;
  }
}

function handleLoadedMetadata() {
  if (videoRef.value) {
    duration.value = videoRef.value.duration;
    isLoaded.value = true;
  }
}

function handleError() {
  isError.value = true;
}

function handleSeek(event: MouseEvent) {
  if (!videoRef.value) return;

  const target = event.currentTarget as HTMLElement;
  const rect = target.getBoundingClientRect();
  const percent = (event.clientX - rect.left) / rect.width;
  videoRef.value.currentTime = percent * duration.value;
}

function toggleFullscreen() {
  if (!videoRef.value) return;

  if (!document.fullscreenElement) {
    videoRef.value.requestFullscreen().catch(() => {
      // Fullscreen not supported or blocked
    });
    isFullscreen.value = true;
  } else {
    document.exitFullscreen();
    isFullscreen.value = false;
  }
}

function handleFullscreenChange() {
  isFullscreen.value = !!document.fullscreenElement;
}

onMounted(() => {
  document.addEventListener('fullscreenchange', handleFullscreenChange);
});

onUnmounted(() => {
  document.removeEventListener('fullscreenchange', handleFullscreenChange);
});
</script>

<template>
  <figure class="help-video">
    <div class="video-container">
      <!-- GIF display (simple image) -->
      <template v-if="isGif">
        <img
          :src="videoPath"
          :alt="video.caption"
          class="gif-image"
          @error="handleError"
        />
      </template>

      <!-- Video player -->
      <template v-else>
        <div v-if="isError" class="video-error">
          <i class="bi bi-exclamation-triangle"></i>
          <span>Video not found</span>
        </div>
        <video
          v-else
          ref="videoRef"
          class="video-element"
          :poster="posterPath"
          preload="metadata"
          @play="handlePlay"
          @pause="handlePause"
          @timeupdate="handleTimeUpdate"
          @loadedmetadata="handleLoadedMetadata"
          @error="handleError"
        >
          <source :src="videoPath" type="video/mp4" />
          Your browser does not support the video tag.
        </video>

        <!-- Play overlay (when paused) -->
        <div
          v-if="!isPlaying && isLoaded"
          class="play-overlay"
          @click="togglePlay"
        >
          <div class="play-button">
            <i class="bi bi-play-fill"></i>
          </div>
        </div>

        <!-- Video controls -->
        <div v-if="isLoaded && !isError" class="video-controls">
          <button class="control-btn" @click="togglePlay">
            <i :class="isPlaying ? 'bi bi-pause-fill' : 'bi bi-play-fill'"></i>
          </button>

          <div class="progress-bar" @click="handleSeek">
            <div class="progress-track">
              <div class="progress-fill" :style="{ width: `${progress}%` }"></div>
            </div>
          </div>

          <span class="time-display">{{ formattedTime }}</span>

          <button class="control-btn" @click="toggleFullscreen">
            <i :class="isFullscreen ? 'bi bi-fullscreen-exit' : 'bi bi-fullscreen'"></i>
          </button>
        </div>
      </template>
    </div>

    <!-- Caption and duration -->
    <figcaption class="video-caption">
      {{ video.caption }}
      <span v-if="video.duration" class="video-duration">
        ({{ video.duration }})
      </span>
    </figcaption>
  </figure>
</template>

<style scoped>
.help-video {
  margin: 16px 0;
}

.video-container {
  position: relative;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  overflow: hidden;
  background: #000;
}

.video-element,
.gif-image {
  display: block;
  width: 100%;
  height: auto;
}

.video-error {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px;
  background: #1f2937;
  color: #ef4444;
}

.video-error i {
  font-size: 32px;
  margin-bottom: 8px;
}

.play-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.3);
  cursor: pointer;
  transition: background-color 0.2s;
}

.play-overlay:hover {
  background: rgba(0, 0, 0, 0.4);
}

.play-button {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 64px;
  height: 64px;
  background: rgba(255, 255, 255, 0.9);
  border-radius: 50%;
  transition: transform 0.2s;
}

.play-overlay:hover .play-button {
  transform: scale(1.1);
}

.play-button i {
  font-size: 28px;
  color: #1f2937;
  margin-left: 4px;
}

.video-controls {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  background: linear-gradient(transparent, rgba(0, 0, 0, 0.8));
}

.control-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: none;
  background: none;
  color: white;
  cursor: pointer;
  border-radius: 4px;
  transition: background-color 0.15s;
}

.control-btn:hover {
  background: rgba(255, 255, 255, 0.2);
}

.control-btn i {
  font-size: 16px;
}

.progress-bar {
  flex: 1;
  height: 24px;
  display: flex;
  align-items: center;
  cursor: pointer;
}

.progress-track {
  width: 100%;
  height: 4px;
  background: rgba(255, 255, 255, 0.3);
  border-radius: 2px;
  overflow: hidden;
}

.progress-fill {
  height: 100%;
  background: #3b82f6;
  border-radius: 2px;
  transition: width 0.1s;
}

.progress-bar:hover .progress-track {
  height: 6px;
}

.time-display {
  font-size: 12px;
  color: rgba(255, 255, 255, 0.8);
  font-family: monospace;
  min-width: 80px;
  text-align: center;
}

.video-caption {
  margin-top: 8px;
  font-size: 13px;
  color: #6b7280;
  font-style: italic;
}

.video-duration {
  color: #9ca3af;
}
</style>

<!-- Dark mode styles (unscoped) -->
<style>
[data-bs-theme="dark"] .video-container {
  border-color: #334155;
}

[data-bs-theme="dark"] .video-caption {
  color: #94a3b8;
}

[data-bs-theme="dark"] .video-duration {
  color: #64748b;
}
</style>
